import { inject, Injectable } from '@angular/core';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OrdersService } from '@us/web/orders/data-access';
import {
    DeliveryAddress,
    DeliveryFee,
    NonNullableGenericState,
    OrderDelivery,
    OrderType,
    SelectorUtil
} from '@us/web/shared/data-access';
import { getErrorMessage } from '@us/web/shared/utils';
import { isNil } from 'lodash';
import { filter, map, of, pairwise, startWith, switchMap, tap } from 'rxjs';
import { selectCart } from './cart.reducer';

interface DeliveryFeesState extends NonNullableGenericState<DeliveryFee[]> {
    selectedDeliveryFee: OrderDelivery | null;
}

@Injectable()
export class DeliveryFeesStore extends ComponentStore<DeliveryFeesState> {
    private readonly store = inject(Store);
    private readonly ordersService = inject(OrdersService);

    readonly params$ = this.store.select(selectCart).pipe(
        startWith(null),
        pairwise(),
        map(([prevCart, cart]) => {
            if (
                cart &&
                cart.order_type === OrderType.Delivery &&
                (isNil(cart.receiver) ||
                    cart.receiver !== prevCart?.receiver ||
                    cart.grand_total !== prevCart?.grand_total ||
                    cart.shop?.id !== prevCart?.shop?.id)
            ) {
                return { receiver: cart.receiver || undefined, total: cart.grand_total, shopId: cart.shop?.id };
            }
            return false;
        }),
        filter(e => e !== false)
    );

    readonly deliveryFees$ = this.params$.pipe(
        tap(params => {
            this.estimateOrderFee(
                params as {
                    receiver: DeliveryAddress | undefined;
                    total: number;
                    shopId: string | undefined;
                }
            );
        }),
        switchMap(() => this.select(s => s.data))
    );

    readonly estimatedOrderFeeArr$ = this.deliveryFees$.pipe(
        map(deliveryFees => {
            return deliveryFees
                .flatMap(item =>
                    (item.estimate_fees || []).map(
                        estimate_fee =>
                            ({
                                partner_id: item.delivery_partner_id,
                                partner_name: item.delivery_partner_name,
                                service_id: estimate_fee.service_id,
                                service_name: estimate_fee.service_name,
                                delivery_fee: estimate_fee.estimate_fee,
                                delivery_fee_original: estimate_fee.estimate_fee_original
                            } as OrderDelivery)
                    )
                )
                .sort((a, b) => a.delivery_fee - b.delivery_fee);
        }),
        tap(items => {
            if (items.length > 0) {
                this.selectDeliveryFee(items[0]);
            }
        })
    );

    readonly selectedDeliveryFee$ = this.select(state => state.selectedDeliveryFee);

    readonly isLoading$ = this.select(SelectorUtil.isLoading);
    readonly isDone$ = this.select(SelectorUtil.isDone);

    readonly selectDeliveryFee = (deliveryFee: OrderDelivery | null) =>
        this.patchState({ selectedDeliveryFee: deliveryFee });

    readonly estimateOrderFee = this.effect<{
        receiver: DeliveryAddress | undefined;
        total: number;
        shopId: string | undefined;
    }>(params$ =>
        params$.pipe(
            tap(() => {
                this.patchState({
                    status: 'loading',
                    error: null
                });
            }),
            switchMap(({ receiver, total, shopId }) =>
                (receiver ? this.ordersService.estimateOrderFee(receiver, total, shopId) : of([])).pipe(
                    tapResponse(
                        items => {
                            this.patchState({
                                data: items,
                                status: 'success',
                                error: ''
                            });
                        },
                        error => {
                            console.error(error);
                            this.patchState({
                                status: 'error',
                                error: getErrorMessage(error)
                            });
                        }
                    )
                )
            )
        )
    );

    constructor() {
        super(<DeliveryFeesState>{
            data: [],
            selectedDeliveryFee: null,
            status: 'pending',
            error: null
        });
    }
}
